// The paint kit of the sea object marks (v2-the-deep.md §11.8, R2652).
//
// Mockup rounds 068 to 074 draw every card with one small kit of canvas
// helpers: a disc, a ring, a line, a path and its stroke or fill, an arc, a
// dotted ring, ticks, brackets, chevrons, and the hull of a field of points.
// This module is that kit, typed, so each card ports point for point into
// `seamine-marks.ts`, `deployable-marks.ts`, `seamine-detonation-looks.ts`,
// `sapper-lay-looks.ts`, `sapper-collect-looks.ts` and `cache-work-looks.ts`.
//
// **An ink is a number** (`0xrrggbb`), as in `render/color.ts`. A painter
// takes the side ink of its object from its caller. The atlas cells of
// `seaobject-cells.ts` paint white, and the layer tints the quad. The few
// inks a card names besides the side ink (the deep water a knock-out takes,
// the pale ink of the water marks, the rock of the terrain) come from the
// generated presentation tables, never from a literal here.
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed.

import {
  CHART, MAP_PALETTE, MARKER_NEUTRAL, NEUTRAL_COLOR, TERRAIN_RIDGE_FILL, TERRAIN_RIDGE_RIM, TERRAIN_RINGS,
  WATER_DEEP_LOW, type ChartStep,
} from '../gen/presentation';
import { rgba } from './color';
import { hexPath as cellHexPath } from './marks';
import { TAU, clamp01 } from './math';
import { smooth } from './recipe-kit';
import { easeInOut, easeOut } from './spawn-plan';

// The arithmetic the cards share with the other recipe kits comes from
// those kits, so each figure has one home.
export { inCubic, lerp, smooth } from './recipe-kit';
export { hash as hh } from './combat-recipe-kit';
export { mulberry32 } from '../world';

/** The context every painter takes. */
export type Ctx = CanvasRenderingContext2D;
/** A point, in map px. */
export type Pt = readonly [number, number];

/** Degrees to radians, as the mockup pages write an angle. */
export const D = (deg: number): number => (deg * Math.PI) / 180;

const hexNum = (css: string): number => parseInt(css.replace('#', ''), 16);

/** The inks a card names besides the side ink. */
export const SEA_INK = {
  /** The deep water: the knock-out inside a solid mark. */
  deep: hexNum(WATER_DEEP_LOW),
  /** The pale ink of the water marks (the chrome text token). */
  pale: MAP_PALETTE.text,
  /** The dim and the faint ink of the chrome, for an unlit pip or track. */
  dim: MAP_PALETTE.dim,
  faint: MAP_PALETTE.faint,
  /** The neutral grey of rock chips and of an object of no side. */
  neutral: NEUTRAL_COLOR,
  /** White, the ink of a hit and of a cell the layer tints. */
  white: MARKER_NEUTRAL,
  /** The rock of the terrain (ui-map-interface.md §4.4). */
  rockFill: TERRAIN_RIDGE_FILL.color,
  rockFillAlpha: TERRAIN_RIDGE_FILL.alpha,
  rockRim: TERRAIN_RIDGE_RIM.color,
  rockRimAlpha: TERRAIN_RIDGE_RIM.alpha,
  rockRings: TERRAIN_RINGS,
} as const;

/** 1 before `a`, 0 after `b`, a smoothstep between. */
export function fade(x: number, a: number, b: number): number { return 1 - smooth(a, b, x); }
/** The clamped cubic ease out of the pages. It takes the `Math.pow` form of
 *  `easeOut` in `spawn-plan.ts`, which can differ in the last bit from the
 *  `u * u * u` form of `outCubic` in `recipe-kit.ts`, so the two stay apart. */
export function outCubic(u: number): number { return easeOut(clamp01(u)); }
/** The clamped cubic ease in and out of the pages. */
export function inOut(u: number): number { return easeInOut(clamp01(u)); }

/** A stroke width in map px, never thinner than 0.9 screen px. */
export const lw = (n: number, z: number): number => Math.max(n, 0.9 / z);
/** A width or a dash that keeps its screen size at every zoom. */
export const px = (n: number, z: number): number => n / z;

const css = (ink: number, a: number): string => rgba(ink, clamp01(a));

/** A filled disc. */
export function disc(c: Ctx, x: number, y: number, r: number, ink: number, a: number): void {
  if (!(r > 0) || !(a > 0)) return;
  c.beginPath(); c.arc(x, y, r, 0, TAU); c.fillStyle = css(ink, a); c.fill();
}

/** A stroked circle. A dashed one takes butt caps, as the pages draw it. */
export function circle(
  c: Ctx, x: number, y: number, r: number, ink: number, a: number, w = 1, dash: readonly number[] = [],
): void {
  if (!(r > 0) || !(a > 0)) return;
  c.beginPath(); c.arc(x, y, r, 0, TAU); c.lineWidth = w; c.strokeStyle = css(ink, a);
  c.lineCap = dash.length ? 'butt' : 'round'; c.setLineDash(dash as number[]); c.stroke(); c.setLineDash([]);
}

/** A straight stroke. */
export function line(
  c: Ctx, x0: number, y0: number, x1: number, y1: number, ink: number, a: number, w = 1,
  dash: readonly number[] = [],
): void {
  if (!(a > 0)) return;
  c.beginPath(); c.moveTo(x0, y0); c.lineTo(x1, y1); c.lineWidth = w;
  c.strokeStyle = css(ink, a); c.lineCap = dash.length ? 'butt' : 'round';
  c.setLineDash(dash as number[]); c.stroke(); c.setLineDash([]);
}

/** An open arc, stroked. */
export function arc(
  c: Ctx, x: number, y: number, r: number, a0: number, a1: number, ink: number, a: number, w: number,
  dash: readonly number[] = [], ccw = false,
): void {
  if (!(r > 0) || !(a > 0)) return;
  c.beginPath(); c.arc(x, y, r, a0, a1, ccw); c.lineWidth = w; c.strokeStyle = css(ink, a);
  c.lineCap = dash.length ? 'butt' : 'round'; c.setLineDash(dash as number[]); c.stroke(); c.setLineDash([]);
}

/** A polyline as an open path. `close` closes it, the default. */
export function P(c: Ctx, pts: readonly Pt[], close = true): void {
  c.beginPath();
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    if (i) c.lineTo(p[0], p[1]); else c.moveTo(p[0], p[1]);
  }
  if (close) c.closePath();
}

/**
 * Strokes the open path. A dashed stroke takes butt caps unless `round`
 * says otherwise: round 068 keeps round caps on every dash.
 */
export function S(
  c: Ctx, ink: number, a = 0.9, w = 1.4, dash: readonly number[] = [], round = false,
): void {
  c.lineWidth = w; c.strokeStyle = css(ink, a); c.lineJoin = 'round';
  c.lineCap = dash.length && !round ? 'butt' : 'round';
  c.setLineDash(dash as number[]); c.stroke(); c.setLineDash([]);
}

/** Fills the open path. */
export function Fl(c: Ctx, ink: number, a = 1): void { c.fillStyle = css(ink, a); c.fill(); }

/** A soft disc: a radial gradient from `a` to nothing. */
export function soft(c: Ctx, x: number, y: number, r: number, ink: number, a: number): void {
  if (!(r > 0) || !(a > 0)) return;
  const g = c.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, css(ink, a)); g.addColorStop(1, css(ink, 0));
  c.beginPath(); c.arc(x, y, r, 0, TAU); c.fillStyle = g; c.fill();
}

/** Text, centred by default. The caller passes words from the locale. */
export function text(
  c: Ctx, s: string, x: number, y: number, font: string, ink: number, a: number,
  align: CanvasTextAlign = 'center', base: CanvasTextBaseline = 'middle',
): void {
  if (!(a > 0)) return;
  c.font = font; c.fillStyle = css(ink, a); c.textAlign = align; c.textBaseline = base; c.fillText(s, x, y);
}

// ---- the words of a mark (R1796, R3249) ------------------------------------------
//
// A word of a mark takes one of the four chart text steps of
// `[chart.text.steps]` and no size of its own (ui-map-interface.md §17.4).
// The mono face sets it at weight 500 and the Fell face in italic 400.

/** The face of a word: the mono face of the modern reading, or the Fell. */
export type WordFace = 'mono' | 'fell';
export type { ChartStep };

/** The size of a step in a face, px at zoom 1: also the size in map px of
 *  a word inside a drawn frame, which grows with the water as its frame
 *  does. */
export function stepPx(face: WordFace, step: ChartStep): number {
  const s = CHART.text.steps[step];
  return face === 'fell' ? s.fellPx : s.monoPx;
}

/**
 * The size in map px at the zoom `z` of a world-placed word: its screen
 * size stays inside the band `band_min` to `band_max` times its step. It
 * is 0 under the zoom gate of the step, and the caller then draws no word.
 */
export function bandPx(face: WordFace, step: ChartStep, z: number): number {
  const t = CHART.text;
  if (!(z > 0) || z < t.steps[step].zoomGate) return 0;
  return (stepPx(face, step) * Math.min(t.bandMax, Math.max(t.bandMin, z))) / z;
}

/** The canvas font of a word of `size` map px in a face and a family. */
export function wordFont(face: WordFace, size: number, family: string): string {
  return face === 'fell' ? `italic 400 ${size}px ${family}` : `500 ${size}px ${family}`;
}

/** A dotted circle: round dots `gap` apart. */
export function dots(c: Ctx, x: number, y: number, r: number, ink: number, a: number, w: number, gap: number): void {
  if (!(r > 0) || !(a > 0)) return;
  c.beginPath(); c.arc(x, y, r, 0, TAU); c.lineWidth = w; c.strokeStyle = css(ink, a); c.lineCap = 'round';
  c.setLineDash([0.01, gap]); c.stroke(); c.setLineDash([]);
}

/** Four corner brackets of a square of half side `r`; each arm is `l` long. */
export function brackets(c: Ctx, r: number, ink: number, a: number, w: number, l: number): void {
  // The corners in the order of the pages: (1, 1), (1, -1), (-1, 1), (-1, -1).
  for (let i = 0; i < 4; i++) {
    const qx = i < 2 ? 1 : -1, qy = i & 1 ? -1 : 1;
    c.beginPath();
    c.moveTo(qx * r, qy * (r - l)); c.lineTo(qx * r, qy * r); c.lineTo(qx * (r - l), qy * r);
    S(c, ink, a, w);
  }
}

/** `n` radial ticks from `r0` to `r1`. */
export function ticks(c: Ctx, r0: number, r1: number, ink: number, a: number, w: number, n: number, rot = 0): void {
  for (let i = 0; i < n; i++) {
    const q = rot + (i * TAU) / n;
    line(c, Math.cos(q) * r0, Math.sin(q) * r0, Math.cos(q) * r1, Math.sin(q) * r1, ink, a, w);
  }
}

/** `n` chevrons at radius `r` that point at the centre. */
export function chevrons(c: Ctx, r: number, n: number, rot: number, ink: number, a: number, w: number, size = 1.5): void {
  for (let i = 0; i < n; i++) {
    const q = rot + (i * TAU) / n, ux = Math.cos(q), uy = Math.sin(q), x = ux * r, y = uy * r, nx = -uy, ny = ux;
    c.beginPath();
    c.moveTo(x + ux * size * 1.1 + nx * size, y + uy * size * 1.1 + ny * size);
    c.lineTo(x, y);
    c.lineTo(x + ux * size * 1.1 - nx * size, y + uy * size * 1.1 - ny * size);
    S(c, ink, a, w);
  }
}

/** One map hex, pointy side up, as the map draws it, as a new path. */
export function hexPath(c: Ctx, x: number, y: number, r: number): void {
  c.beginPath(); cellHexPath(c, x, y, r);
}

/** The convex hull of a set of points. */
function convexHull(pts: readonly Pt[]): Pt[] {
  const p = [...pts].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const cross = (o: Pt, a: Pt, b: Pt): number => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lo: Pt[] = [], hi: Pt[] = [];
  for (const q of p) {
    while (lo.length >= 2 && cross(lo[lo.length - 2], lo[lo.length - 1], q) <= 0) lo.pop();
    lo.push(q);
  }
  for (let i = p.length - 1; i >= 0; i--) {
    const q = p[i];
    while (hi.length >= 2 && cross(hi[hi.length - 2], hi[hi.length - 1], q) <= 0) hi.pop();
    hi.push(q);
  }
  hi.pop(); lo.pop();
  return lo.concat(hi);
}

/** The convex hull of a set of points, held `r` clear of each point. */
export function padHull(pts: readonly Pt[], r: number): Pt[] {
  const q: Pt[] = [];
  for (const p of pts) {
    for (let i = 0; i < 16; i++) {
      const a = (i * Math.PI) / 8;
      q.push([p[0] + Math.cos(a) * r, p[1] + Math.sin(a) * r]);
    }
  }
  return convexHull(q);
}

/** Walks a closed polygon and calls `fn(x, y, nx, ny)` each `step` px; n is
 *  the inward normal. */
export function walkPoly(
  poly: readonly Pt[], step: number, fn: (x: number, y: number, nx: number, ny: number) => void,
): void {
  let cx = 0, cy = 0;
  for (const p of poly) { cx += p[0]; cy += p[1]; }
  cx /= poly.length; cy /= poly.length;
  let carry = step / 2;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length], dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy);
    if (L < 1e-6) continue;
    let nx = -dy / L, ny = dx / L;
    const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
    if ((cx - mx) * nx + (cy - my) * ny < 0) { nx = -nx; ny = -ny; }
    for (let d = carry; d < L; d += step) fn(a[0] + (dx * d) / L, a[1] + (dy * d) / L, nx, ny);
    carry = (carry - L) % step;
    if (carry < 0) carry += step;
  }
}

/** The topmost point of a polygon (the least y). */
export function topOf(poly: readonly Pt[]): Pt {
  let t = poly[0];
  for (const p of poly) if (p[1] < t[1]) t = p;
  return t;
}

/** A point turned by `a` about the origin. */
export function rot(p: Pt, a: number): Pt {
  return [p[0] * Math.cos(a) - p[1] * Math.sin(a), p[0] * Math.sin(a) + p[1] * Math.cos(a)];
}
